import { useState, useEffect, useCallback } from "react";
import { disruptionAPI } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Progress } from "./ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { toast } from "sonner";
import QuestionPhaseView from "./disruption/QuestionPhaseView";
import BrainstormView from "./disruption/BrainstormView";
import DotVotingView from "./disruption/DotVotingView";
import DisruptionResults from "./disruption/DisruptionResults";
import PresentationMode from "./disruption/PresentationMode";
import {
  Skull, Rocket, Users, Play, ChevronRight, Settings, Loader2, Lightbulb, Vote, Trophy,
  Presentation, ArrowLeft, ChevronLeft,
} from "lucide-react";

const PHASE_CONFIG = {
  setup: { label: "Setup", icon: Settings, color: "text-slate-500" },
  phase1: { label: "Phase 1: The Attack", icon: Skull, color: "text-red-600" },
  phase2: { label: "Phase 2: Company 2.0", icon: Rocket, color: "text-indigo-600" },
  brainstorm: { label: "Brainstorm", icon: Lightbulb, color: "text-amber-600" },
  voting: { label: "Dot Voting", icon: Vote, color: "text-violet-600" },
  completed: { label: "Results", icon: Trophy, color: "text-emerald-600" },
};

const PHASES_ORDER = ["setup", "phase1", "phase2", "brainstorm", "voting", "completed"];

export const DisruptionSession = ({ boardId, board }) => {
  const { user } = useAuth();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [responses, setResponses] = useState([]);
  const [presenting, setPresenting] = useState(false);
  const [viewOverride, setViewOverride] = useState(null); // null = follow current_phase, or "brainstorm"/"voting"/"phase1"/"phase2"/"completed"
  const isOwner = board?.created_by === user?.id;

  const fetchSession = useCallback(async () => {
    try {
      const res = await disruptionAPI.getSession(boardId);
      setSession(res.data);
    } catch {
      setSession(null);
    } finally {
      setLoading(false);
    }
  }, [boardId]);

  const fetchResponses = useCallback(async () => {
    try {
      const res = await disruptionAPI.getMyResponses(boardId);
      setResponses(res.data.responses || []);
    } catch { /* silent */ }
  }, [boardId]);

  useEffect(() => { fetchSession(); }, [fetchSession]);
  useEffect(() => { if (session) fetchResponses(); }, [session, fetchResponses]);

  const createSession = async () => {
    try {
      const res = await disruptionAPI.createSession(boardId);
      setSession(res.data);
      toast.success("Radical Disruption session created!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create session");
    }
  };

  const startSession = async () => {
    try {
      await disruptionAPI.assignPairs(boardId, { mode: "auto" });
      await disruptionAPI.assignGroups(boardId, { mode: "auto", group_size: 4 });
      await disruptionAPI.advancePhase(boardId, "phase1");
      fetchSession();
      toast.success("Session started! Phase 1: The Attack");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to start");
    }
  };

  const advancePhase = async (nextPhase) => {
    try {
      await disruptionAPI.advancePhase(boardId, nextPhase);
      fetchSession();
      toast.success(`Advanced to ${PHASE_CONFIG[nextPhase]?.label || nextPhase}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to advance");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  // No session yet — show creation
  if (!session) {
    return (
      <div className="max-w-2xl mx-auto py-16 px-6 text-center" data-testid="disruption-setup">
        <div className="w-16 h-16 bg-red-50 rounded-2xl flex items-center justify-center mx-auto mb-5">
          <Skull className="w-8 h-8 text-red-500" />
        </div>
        <h2 className="text-3xl font-heading font-bold text-slate-900 tracking-tight mb-3">
          Part 2: Radical Disruption
        </h2>
        <p className="text-slate-500 text-base leading-relaxed max-w-lg mx-auto mb-8">
          Adopt the persona of a Pirate or Founder. Stress-test your business against disruption, then reimagine it with AI at the core. Work in pairs, present findings, and brainstorm bold new ideas.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          {[
            { icon: Skull, label: "Phase 1: The Attack", desc: "21 questions as the Pirate", color: "text-red-500 bg-red-50" },
            { icon: Rocket, label: "Phase 2: Company 2.0", desc: "22 questions as the Founder", color: "text-indigo-500 bg-indigo-50" },
            { icon: Lightbulb, label: "Brainstorm & Vote", desc: "Generate and prioritize ideas", color: "text-amber-500 bg-amber-50" },
          ].map((item, i) => (
            <div key={i} className="bg-white rounded-xl border border-slate-200 p-5">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center mx-auto mb-3 ${item.color}`}>
                <item.icon className="w-5 h-5" />
              </div>
              <div className="font-heading font-semibold text-sm text-slate-900">{item.label}</div>
              <div className="text-xs text-slate-400 mt-1">{item.desc}</div>
            </div>
          ))}
        </div>
        {isOwner ? (
          <Button
            data-testid="create-disruption-btn"
            onClick={createSession}
            className="bg-red-600 hover:bg-red-700 text-white h-11 px-8 font-medium active:scale-[0.98]"
          >
            <Skull className="w-4 h-4 mr-2" />
            Launch Radical Disruption
          </Button>
        ) : (
          <p className="text-sm text-slate-400">The session organizer needs to launch Part 2.</p>
        )}
      </div>
    );
  }

  // Session exists but in setup
  if (session.status === "setup" || session.current_phase === "setup") {
    return (
      <div className="max-w-2xl mx-auto py-12 px-6 text-center" data-testid="disruption-ready">
        <div className="w-14 h-14 bg-indigo-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <Play className="w-7 h-7 text-indigo-600" />
        </div>
        <h2 className="text-2xl font-heading font-bold text-slate-900 mb-2">Session Ready</h2>
        <p className="text-slate-500 mb-2 text-sm">
          {board?.participants?.length || 1} participant(s) will be auto-assigned into pairs and brainstorm groups.
        </p>
        <p className="text-slate-400 mb-6 text-xs">
          43 questions across 14 categories. Phase 1 (Pirate) + Phase 2 (Founder) + Brainstorm + Dot-Vote.
        </p>
        {isOwner ? (
          <Button
            data-testid="start-disruption-btn"
            onClick={startSession}
            className="bg-indigo-600 hover:bg-indigo-700 text-white h-11 px-8 font-medium active:scale-[0.98]"
          >
            <Play className="w-4 h-4 mr-2" />
            Start Session
          </Button>
        ) : (
          <p className="text-sm text-slate-400">Waiting for the facilitator to start...</p>
        )}
      </div>
    );
  }

  const currentPhaseIdx = PHASES_ORDER.indexOf(session.current_phase);
  const nextPhase = PHASES_ORDER[currentPhaseIdx + 1] || null;
  const phaseConfig = PHASE_CONFIG[session.current_phase] || PHASE_CONFIG.phase1;
  const PhaseIcon = phaseConfig.icon;

  // Determine which view to render
  const activeView = viewOverride || session.current_phase;
  const activeViewConfig = PHASE_CONFIG[activeView] || phaseConfig;
  const ActiveViewIcon = activeViewConfig.icon;

  // Find user's pair
  const myPair = session.pairs?.find((p) =>
    p.members?.some((m) => m.user_id === user?.id)
  );

  return (
    <div className="flex flex-col h-full" data-testid="disruption-active">
      {/* Phase Header */}
      <div className="bg-white border-b border-slate-200 px-5 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {viewOverride && (
              <Button variant="ghost" size="sm" data-testid="back-to-current-btn" onClick={() => setViewOverride(null)} className="h-8 px-2 text-slate-400 hover:text-slate-600 mr-1">
                <ArrowLeft className="w-4 h-4" />
              </Button>
            )}
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
              activeView === "phase1" ? "bg-red-50" :
              activeView === "phase2" ? "bg-indigo-50" :
              activeView === "brainstorm" ? "bg-amber-50" :
              activeView === "voting" ? "bg-violet-50" : "bg-emerald-50"
            }`}>
              <ActiveViewIcon className={`w-5 h-5 ${activeViewConfig.color}`} />
            </div>
            <div>
              <h3 className="font-heading font-semibold text-sm text-slate-900">{activeViewConfig.label}</h3>
              {myPair && (activeView === "phase1" || activeView === "phase2") && (
                <span className="text-[10px] text-slate-400">Pair: {myPair.members?.map((m) => m.name).join(" & ")}</span>
              )}
              {viewOverride && (
                <span className="text-[10px] text-indigo-500">Viewing previous phase</span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Phase step dots */}
            <div className="flex items-center gap-1 mr-2">
              {PHASES_ORDER.slice(1, -1).map((p, i) => (
                <div key={p} className={`w-2 h-2 rounded-full ${PHASES_ORDER.indexOf(p) <= currentPhaseIdx ? "bg-indigo-500" : "bg-slate-200"}`} />
              ))}
            </div>

            {/* Facilitator: Previous phase */}
            {!viewOverride && isOwner && currentPhaseIdx > 1 && (
              <Button data-testid="prev-phase-btn" onClick={() => advancePhase(PHASES_ORDER[currentPhaseIdx - 1])} size="sm" variant="outline" className="h-8 text-xs border-slate-300">
                <ChevronLeft className="w-3.5 h-3.5 mr-1" />Back
              </Button>
            )}

            {/* Facilitator: Next phase */}
            {!viewOverride && isOwner && nextPhase && (
              <Button data-testid="advance-phase-btn" onClick={() => advancePhase(nextPhase)} size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white h-8 text-xs">
                {PHASE_CONFIG[nextPhase]?.label || "Next"}<ChevronRight className="w-3.5 h-3.5 ml-1" />
              </Button>
            )}

            {/* Present button */}
            {!viewOverride && isOwner && (activeView === "phase1" || activeView === "phase2") && session.pairs?.length > 0 && (
              <Button data-testid="present-btn" onClick={() => setPresenting(true)} size="sm" variant="outline" className="h-8 text-xs border-slate-300 text-slate-600 hover:bg-slate-100">
                <Presentation className="w-3.5 h-3.5 mr-1.5" />Present
              </Button>
            )}
          </div>
        </div>

        {/* Facilitator Phase Control Bar — jump to any phase */}
        {isOwner && (
          <div className="flex items-center gap-1 mt-2 pt-2 border-t border-slate-100 overflow-x-auto">
            {PHASES_ORDER.slice(1).map((p) => {
              const conf = PHASE_CONFIG[p];
              const PIcon = conf.icon;
              const isCurrent = session.current_phase === p && !viewOverride;
              const isViewing = viewOverride === p;
              return (
                <button key={p} data-testid={`jump-${p}`}
                  onClick={() => {
                    if (p === session.current_phase) {
                      setViewOverride(null);
                    } else {
                      if (window.confirm(`Move all players to "${conf.label}"? This changes the active phase for everyone.`)) {
                        advancePhase(p);
                        setViewOverride(null);
                      }
                    }
                  }}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium whitespace-nowrap transition-colors ${
                    isCurrent ? "bg-indigo-100 text-indigo-700 ring-1 ring-indigo-300" :
                    isViewing ? "bg-slate-100 text-slate-600" :
                    PHASES_ORDER.indexOf(p) < currentPhaseIdx ? "text-emerald-600 hover:bg-emerald-50" :
                    "text-slate-400 hover:bg-slate-50 hover:text-slate-600"
                  }`}>
                  {PHASES_ORDER.indexOf(p) < currentPhaseIdx ? (
                    <span className="w-3.5 h-3.5 rounded-full bg-emerald-500 flex items-center justify-center"><span className="text-[7px] text-white font-bold">&#10003;</span></span>
                  ) : (
                    <PIcon className="w-3.5 h-3.5" />
                  )}
                  {conf.label}
                  {isCurrent && <span className="ml-1 w-1.5 h-1.5 rounded-full bg-indigo-500 animate-pulse" />}
                </button>
              );
            })}
          </div>
        )}

        {/* Non-owner: View navigation — shows when completed or voting */}
        {!isOwner && (session.current_phase === "completed" || session.current_phase === "voting") && (
          <div className="flex items-center gap-1 mt-2 pt-2 border-t border-slate-100 overflow-x-auto">
            {[
              { key: null, label: session.current_phase === "completed" ? "Results" : PHASE_CONFIG[session.current_phase]?.label, icon: session.current_phase === "completed" ? Trophy : PHASE_CONFIG[session.current_phase]?.icon },
              { key: "phase1", label: "Q&A Phase 1", icon: Skull },
              { key: "phase2", label: "Q&A Phase 2", icon: Rocket },
              { key: "brainstorm", label: "Brainstorm Board", icon: Lightbulb },
              { key: "voting", label: "Dot Voting", icon: Vote },
            ].filter((t) => t.key !== session.current_phase).map((tab) => {
              const isActive = viewOverride === tab.key || (!viewOverride && tab.key === null);
              const TabIcon = tab.icon;
              return (
                <button key={tab.key || "current"} data-testid={`nav-${tab.key || "current"}`}
                  onClick={() => setViewOverride(tab.key)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium whitespace-nowrap transition-colors ${
                    isActive ? "bg-indigo-50 text-indigo-700" : "text-slate-400 hover:bg-slate-50 hover:text-slate-600"
                  }`}>
                  <TabIcon className="w-3.5 h-3.5" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Phase Content */}
      <div className={`flex-1 ${activeView === "brainstorm" ? "overflow-hidden" : "overflow-y-auto"}`}>
        {(activeView === "phase1" || activeView === "phase2") && (
          <QuestionPhaseView
            boardId={boardId}
            phase={activeView}
            questions={session.questions?.[activeView]}
            myPair={myPair}
            responses={responses}
            onResponseSaved={fetchResponses}
            isOwner={isOwner}
            gameMode={board?.game_mode}
            onAdvancePhase={activeView === "phase1" ? () => advancePhase("phase2") : activeView === "phase2" ? () => advancePhase("brainstorm") : null}
            nextPhaseLabel={activeView === "phase1" ? "Phase 2: Company 2.0" : activeView === "phase2" ? "Brainstorm" : null}
          />
        )}
        {activeView === "brainstorm" && (
          <BrainstormView
            boardId={boardId}
            session={session}
            userId={user?.id}
            onSessionRefresh={fetchSession}
          />
        )}
        {activeView === "voting" && (
          <DotVotingView
            boardId={boardId}
            maxVotes={session.settings?.dot_votes_per_person || 5}
            userId={user?.id}
          />
        )}
        {activeView === "completed" && (
          <DisruptionResults boardId={boardId} />
        )}
      </div>

      {/* Presentation Mode Overlay */}
      {presenting && (
        <PresentationMode
          boardId={boardId}
          session={session}
          isOwner={isOwner}
          onClose={() => setPresenting(false)}
        />
      )}
    </div>
  );
};

export default DisruptionSession;
